<template>
  <div class="w-full">
    <label for="users_emails" class="block text-sm font-medium text-gray-700">
      Invite {{ userType }}s
    </label>
    <div class="flex gap-x-2">
      <v-select
        :data-cy="`${userType}-invitation`"
        class="
          mt-1
          block
          w-full
          border-gray-300
          focus:ring-vice-teal-500
          focus:border-vice-teal-500
          rounded-md
          shadow-sm
          sm:text-sm
          text-gray-900
        "
        id="users_emails"
        placeholder="Type or select email addresses to invite"
        ref="usersSelect"
        @search="onSearch"
        :disabled="!canAddUsers"
        label="email"
        :reduce="({ email }) => email"
        :options="clinicianUniversityStudents"
        @option:selected="selectedOption"
        :create-option="onCreateOption"
        @keyup.enter="addMultipleUsers"
      >
        <template v-slot:no-options>
          We cannot find users within your organization with these emails. To add external
          participants, enter their full email address and click "Add"
        </template>
        <template #open-indicator>
          <!-- Custom dropdown icon -->
          <span style="margin-right: 4px">
            <SelectDropdownArrow class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </template>
      </v-select>
      <div
        @click="addMultipleUsers"
        :class="{
          'opacity-50 cursor-not-allowed': !canAddUsers,
        }"
        class="
          rounded
          w-20
          cursor-pointer
          bg-vice-teal-400
          flex
          justify-center
          items-center
          text-sm text-white
          hover:bg-vice-teal-500
          add-button
        "
      >
        <!-- <PlusIcon class="w-5 pr-1" /> -->
        ADD
      </div>
    </div>
    <div class="my-4 border rounded-lg p-px shadow">
      <table class="table-fixed w-full divide-y divide-gray-200">
        <thead class="bg-gray-100">
          <tr>
            <th
              scope="col"
              class="
                w-1/4
                md:w-1/3
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Email
            </th>
            <th
              scope="col"
              class="
                w-1/4
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Invite status
            </th>
            <th
              scope="col"
              class="
                w-1/5
                whitespace-nowrap
                md:p-4
                p-2
                text-right text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Options
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(invitedUser, index) in selectedOptions"
            :key="invitedUser.email"
            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
          >
            <td class="px-2 md:px-4 py-1 md:w-2/3 w-full text-left">
              <div class="font-medium text-gray-600 text-xxs md:text-xs break-words">
                {{ invitedUser.email }}
              </div>
            </td>
            <td class="px-0 md:px-4 py-1 w-1/4 whitespace-nowrap">
              <span class="flex gap-1 items-center" v-if="invitedUser.accepted_at">
                <ViceBadge green small>Already accepted</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else-if="!invitedUser.email_sent">
                <ViceBadge indigo small>Sending email</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else>
                <ViceBadge yellow small>Invitation sent</ViceBadge>
              </span>
            </td>

            <td class="px-4 py-1 w-1/4 whitespace-nowrap text-gray-500">
              <a
                href="javascript:void(0)"
                :data-cy="`remove-${userType}`"
                @click="removeInvite(invitedUser)"
              >
                <ViceButton small outline red icon="UserRemoveIcon">Remove</ViceButton>
              </a>
            </td>
          </tr>

          <tr v-if="!selectedOptions.length">
            <td colspan="3" class="p-4 w-2/3 whitespace-nowrap text-center">
              <span class="text-xs text-gray-500">No {{ userType }}s invited yet!</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button
      type="button"
      class="
        cursor-pointer
        relative
        flex
        justify-center
        items-center
        flex-col
        w-full
        border-2 border-gray-300 border-dashed
        rounded-md
        p-5
        text-center
        hover:border-gray-400
        group
      "
    >
      <div class="flex items-center justify-center">
        <div class="flex space-x-2">
          <div class="flex items-center">
            <UploadIcon class="w-7 text-gray-300 group-hover:text-gray-400" />
          </div>
          <div class="flex items-center">
            <div class="text-left">
              <span class="text-sm font-medium text-gray-700 group-hover:text-gray-500 block">
                <span class="font-bold">
                  <span class="text-vice-teal-700 underline">Click to upload</span>
                  or drag and drop
                </span>
                <br />
                to invite multiple participants
              </span>

              <span class="mt-2 text-sm font-medium text-gray-500 group-hover:text-gray-400 block">
                CSV (max. 3MB)
              </span>
            </div>
          </div>
        </div>
      </div>
      <input
        :data-cy="`csv-${userType}-invitation`"
        type="file"
        ref="csvFileInput"
        multiple
        accept=".csv"
        @change="onCsvFileUpload"
        class="cursor-pointer opacity-0 absolute h-full w-full"
      />
    </button>

    <a
      data-cy="download-sample"
      href="/user_emails.csv"
      download="user_emails.csv"
      class="text-vice-teal-600 underline text-sm font-bold block mt-3"
      v-if="userType == 'participant'"
    >
      Download .csv sample
    </a>
    <br />
    <span class="text-red-500 text-sm break-all" v-if="error">{{ error }}</span>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Papa from 'papaparse';
import axios from 'axios';
import UploadIcon from '@/Components/Icons/UploadIcon.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import userType from '@/Composables/Enums/userType';
import SelectDropdownArrow from '@/Components/Icons/SelectDropdownArrow.vue';

export default {
  name: 'UserInvitation',
  components: {
    ViceBadge,
    ViceButton,
    vSelect,
    UploadIcon,
    SelectDropdownArrow,
  },
  props: {
    userType: {
      type: String,
      required: true,
    },
    maxAmount: {
      type: Number,
      required: true,
    },
    takenUsers: {
      required: true,
    },
    clinicianUniversityStudents: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    allOptions: [],
    selectedOptions: [],
    pre_selected_list: [],
    error: '',
    extractedEmailsList: [],
  }),
  computed: {
    errorMessages() {
      return {
        maxExceeded: `E-mail invitations exceed the number of ${this.userType}s set for this session. Please increase the number of ${this.userType}s.`,
        alreadyAdded: 'E-mail is already added.',
      };
    },
    canAddUsers() {
      return this.selectedOptions.length < this.maxAmount;
    },
    deselect_list() {
      const allEmails = this.selectedOptions.map((user) => user.email);
      return this.pre_selected_list.filter((value) => !allEmails.includes(value));
    },
    new_selected_list() {
      const allEmails = this.selectedOptions.map((user) => user.email);
      return allEmails.filter((value) => !this.pre_selected_list.includes(value));
    },
    options() {
      // Return options that are not already selected or taken
      return this.clinicianUniversityStudents.filter((value) => {
        const selected =
          Object.keys(value).length && // check if there are options
          // remove already selected emails
          !this.selectedOptions.some((_selected) => _selected.email == value.email) &&
          // remove emals selected in the other table
          !this.takenUsers?.some((email) => email == value.email);

        return selected;
      });
    },
  },

  watch: {
    selectedOptions: {
      deep: true,
      handler() {
        const userEmails = {
          new_selected_list: this.new_selected_list,
          deselected_list: this.deselect_list,
          selected_list: this.pre_selected_list,
        };
        this.$emit('userEmailsChanged', userEmails);
      },
    },
  },
  mounted() {
    this.fetchExistingInvitations();

    fetch(route('user.emails.list'), {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
    })
      .then((response) => response.json())
      .then((result) => {
        this.allOptions = result.map((email) => ({
          email,
          email_sent: false,
        }));
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  },

  methods: {
    fetchExistingInvitations() {
      if (!this.$page.props.session) return;

      axios.post(route('user.invite.list', this.$page.props.session.id)).then((response) => {
        const usersList = `${this.userType}_list`;

        // TODO: Convert array to object
        if (response.data[usersList].constructor.name == 'Object');
        response.data[usersList] = Object.values(response.data[usersList]);

        this.pre_selected_list = response.data[usersList]?.map((invite) => invite.email);
        this.selectedOptions = response.data[usersList];
      });
    },
    addMultipleUsers() {
      if (this.extractedEmailsList.length + this.selectedOptions.length > this.maxAmount) {
        this.error = this.errorMessages.maxExceeded;
        return;
      }
      this.extractedEmailsList.forEach((email) => {
        if (!this.takenUsers.includes(email)) {
          this.selectedOptions.push({ email, email_sent: false });
          this.$refs.usersSelect.search = '';
        } else this.error = this.errorMessages.alreadyAdded;
      });
    },
    onSearch(search, loading) {
      this.error = '';
      this.selectedOptions = _.uniqBy(this.selectedOptions, (e) => e.email);
      if (!search.length) return;
      this.extractedEmailsList = _.uniq(
        search.split(/[\s,;]+/).filter((email) => this.validateEmail(email)),
      );
      loading(true);
      this.search(loading, search, this);
    },

    search: _.debounce((loading, search, vm) => {
      axios.post(route('user.emails.list'), { query: search }).then((response) => {
        vm.allOptions = response.data.map((email) => ({ email, email_sent: false }));
        loading(false);
      });
    }, 350),

    // eslint-disable-next-line consistent-return
    onCreateOption(email) {
      if (this.validateEmail(email)) {
        return { email, email_sent: false };
      }
      // TODO: Add all users
      // this.allOptions = [];
    },

    removeInvite(invitedUser) {
      this.selectedOptions = this.selectedOptions.filter(
        (users) => users.email != invitedUser.email,
      );
    },

    validateEmail(email) {
      return Boolean(
        email.match(
          // eslint-disable-next-line no-useless-escape
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        ),
      );
    },

    selectedOption(value) {
      value.email = value.email.toLowerCase();
      if (this.validateEmail(value.email)) {
        const index = this.selectedOptions.findIndex(({ email }) => email === value.email);
        if (index >= 0) {
          this.selectedOptions.splice(index, 1);
        } else {
          this.selectedOptions.push(value);
        }
        this.$refs.usersSelect.clearSelection();
      }
    },

    onCsvFileUpload(e) {
      const self = this;
      Papa.parse(e.target.files[0], {
        delimiter: ',',
        header: true,
        skipEmptyLines: true,
        complete(results) {
          if (results.errors.length) {
            self.error = results.errors[0].message;
          } else {
            self.tidyCsvData(results.data);
          }
        },
      });
      this.$refs.csvFileInput.value = null;
    },

    tidyCsvData(csvData) {
      const invalidEmails = [];
      if (csvData.length + this.selectedOptions.length > this.maxAmount) {
        if (this.userType == userType.participant) {
          this.error =
            'E-mail invitations exceed the number of participants set for this session. Please increase number of participants.';
        } else if (this.userType == userType.observer) {
          this.error =
            'E-mail invitations exceed the number of observers set for this session. Please increase number of observers.';
        }
        return;
      }
      this.error = '';
      csvData.forEach((row) => {
        row.email = row.email.replace(/ /g, '').toLowerCase();
        if (this.validateEmail(row.email)) {
          if (!this.takenUsers?.some((takenEmail) => takenEmail == row.email)) {
            this.selectedOption(row);
          }
        } else {
          invalidEmails.push(row);
          this.error = `Invalid email: ${invalidEmails.map((email) => email.email)}`;
        }
      });
    },
  },
};
</script>

<style lang="scss">
::placeholder {
  color: rgb(136, 139, 136) !important;
  font-size: 14px;
}

.vs__search,
.vs__dropdown-toggle,
.vs__selected-options {
  background: none;
}

.vs__search {
  width: 100%;
  margin-top: 0.25rem;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
}

.vs__actions {
  cursor: pointer;
}

.vs__dropdown-menu {
  background: #ffffff;
  color: var(--ViCE---Dark-Grey, #606367);
  font-size: 14px;
  & .vs__dropdown-option--highlight {
    background-color: rgba(45, 212, 191, var(--tw-bg-opacity));
  }
}

.vs--searchable .vs__dropdown-toggle {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
  border-color: b;
  height: 38px;
}

.vs--open .vs__dropdown-toggle {
  border-bottom-color: #d1d5db;
  font-size: 14px;
  color: rgb(136, 139, 136) !important;
}

.add-button {
  height: 38px;
  margin-top: 4px;
}
</style>
